import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { isDeploymentUXEnabled } from '@common/utils/hooks/flags';
import { titleCase } from '@common/utils/metadataUtils';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Container, Stack, TabPane, Tabs } from '@prophecy/ui-v3';
import { TAB_CONTENT_WRAPPER_CLASS_NAME } from '@prophecy/ui-v3/Tabs';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useAppMetadata } from '../../context/appMetadata';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useUnsecuredGlobalVariables } from '../../context/globalConfig/unsecuredConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { useUser } from '../../context/user';
import AdminSettings from './Admin/AdminSettings';
import SSO from './Admin/SSO';
import { About } from './components/About';
import { AccessTokens } from './components/AccessTokens';
import { GitTab } from './components/GitTab';
import { Info } from './components/Info';
import Usage from './components/Usage';
import { SettingPageLayout } from './config/settings';
import { AccessTokenProvider } from './contexts/accessTokensContext';
import Deployment from './Deployment';

// Only the usage tab scrolls at the window edge; every other tab keeps the container's own scroll.
const SettingsContainer = styled(Container)<{ $scrollAtWindowEdge: boolean }>`
  ${({ $scrollAtWindowEdge }) =>
    $scrollAtWindowEdge &&
    `
      height: auto;
      min-height: 100%;
      overflow: visible;

      .${TAB_CONTENT_WRAPPER_CLASS_NAME} {
        overflow: visible;
      }
    `}
`;

export default function Settings() {
  const { tab } = useParams<ExtractUrlPathVariables<typeof Private_Routes.Settings.tab.url>>();
  const user = useUser();
  const globalConf = useGlobalVariables();
  const userInfo = useAppMetadata()?.user;
  const isAdmin = userInfo?.type === UserTypes.ClusterAdmin || userInfo?.type === UserTypes.Support;
  const isSupportUser = userInfo?.type === UserTypes.Support;
  const navigate = useNavigate();
  const isAboutPageEnabled = globalConf.isAboutProphecyEnabled;
  const isCreditsEnabled = globalConf.isCreditsEnabled;
  const isSapEnabled = globalConf.isSapEnabled;
  const unsecureConfig = useUnsecuredGlobalVariables();
  const isAuthEnabled = unsecureConfig.isConfigurableAuthEnabled;
  const isSaas = ((globalConf.getCloudProvider as string)?.toLowerCase() as string) === 'saas';

  const isTabHidden = useCallback(
    (entity: string) => {
      const isSSOEnabled = (isAuthEnabled && isSaas) || isAdmin;

      return (
        (entity === 'admin' && !isAdmin) ||
        // Under SAP non-admins get the usage tab too, except its admin-only sections.
        (entity === 'usage' && ((!isAdmin && !isSapEnabled) || !isCreditsEnabled)) ||
        (entity === 'about' && !isAboutPageEnabled) ||
        (entity === 'sso' && !isSSOEnabled) ||
        (entity === 'deployment' && !isDeploymentUXEnabled(user.user))
      );
    },
    [isAuthEnabled, isSaas, isAdmin, isSapEnabled, isCreditsEnabled, isAboutPageEnabled, user.user]
  );

  const [activeTab, setActiveTab] = useState(!tab || isTabHidden(tab) ? SettingPageLayout.firstTab : tab);

  const { track } = useMixPanel();

  useEffect(() => {
    track?.(EntityEvents[Entity.Team].metadata.getTabChange(activeTab));
  }, [track, activeTab]);

  useEffect(() => {
    setActiveTab(!tab || isTabHidden(tab) ? SettingPageLayout.firstTab : tab);
  }, [tab, isTabHidden]);

  const onChange = (currentTab: string) => {
    if (isTabHidden(currentTab)) {
      return;
    }
    setActiveTab(currentTab);
    navigate(Private_Routes.Settings.tab.getUrl({ tab: currentTab }));
    track(EntityEvents.settings.home.tabChange, {
      name: user?.user?.firstName,
      tab: currentTab,
      url: window.location.href
    });
  };

  const getTabDetail = useCallback(
    (entity: string) => {
      if (entity === 'about') {
        return <About />;
      } else if (entity === 'admin') {
        return <AdminSettings isSupportUser={isSupportUser} />;
      } else if (entity === 'sso') {
        return <SSO />;
      } else if (entity === 'git') {
        return <GitTab />;
      } else if (entity === 'accessTokens') {
        return (
          <AccessTokenProvider>
            <AccessTokens />
          </AccessTokenProvider>
        );
      } else if (entity === 'usage') {
        return <Usage />;
      } else if (entity === 'deployment') {
        return <Deployment />;
      } else {
        return <Info />;
      }
    },
    [isSupportUser]
  );

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12} align='center' alignY='center'>
      <DocumentTitle title={`Settings: ${titleCase(activeTab || Entity.Team)}`} />
      <SettingsContainer noPadding={true} $scrollAtWindowEdge={activeTab === 'usage'}>
        <Tabs activeKey={activeTab} onChange={onChange}>
          {SettingPageLayout.tabs
            .filter(({ entity }) => !isTabHidden(entity))
            .map(({ title, entity }) => {
              return (
                <TabPane key={entity} tab={title}>
                  {getTabDetail(entity)}
                </TabPane>
              );
            })}
        </Tabs>
      </SettingsContainer>
    </Stack>
  );
}
